import { Component, inject, OnInit, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CompanyService} from '@lib-services/company.service';
import {MessageService} from '@lib-services/message.service';
import {ICompany, ICompanyCreate, ICompanyUpdate} from '@lib-typing/company.interface';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import {Company} from '@libs-classes/company.class';
import {LoaderComponent} from '@lib-ui/loader/loader.component';
import { CloseButtonComponent } from '@lib-ui/close-button/close-button.component';
import { filter, map, Observable, startWith, switchMap } from 'rxjs';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect } from '@angular/material/select';
import { CashProviderEnum } from '@libs-enums/cash-provider.enum';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'app-company-form',
    standalone: true,
    imports: [
        MatButton,
        MatCheckbox,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        MatDialogContent,
        LoaderComponent,
        CloseButtonComponent,
        MatDialogTitle,
        MatAutocomplete,
        MatOption,
        AsyncPipe,
        MatAutocompleteTrigger,
        MatTooltip,
        MatSelect,
    ],
    templateUrl: './company-form.component.html',
    styleUrl: './company-form.component.scss'
})
export class CompanyFormComponent implements OnInit {
    companyForm: FormGroup;
    company = new Company({} as ICompany);
    loading = signal(false);
    public data: { companyId?: number } = inject(MAT_DIALOG_DATA);
    timezones: string[] = Intl.supportedValuesOf('timeZone');
    cashProviders = Object.values(CashProviderEnum);
    filteredTimezones: Observable<string[]> | undefined;

    constructor(
        private fb: FormBuilder,
        private companyService: CompanyService,
        private messageService: MessageService,
        protected dialogRef: MatDialogRef<CompanyFormComponent>

    ) {
        this.companyForm = this.fb.group({
            name: ['', Validators.required],
            paymentDevice: [false],
            canExecutorChangePrice: [false],
            canExecutorAddEquipment: [false],
            timeZone: ['', Validators.required],
            cashProvider: [CashProviderEnum.aqsi],
            cashProviderConfigJson: [''],
        });
    }

    ngOnInit(): void {
        const initialTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        this.filteredTimezones = this.companyForm.controls['timeZone'].valueChanges.pipe(
            startWith(initialTimezone),
            map(value => this.filter(value || '')),
        );

        this.companyForm.controls['timeZone'].setValue(initialTimezone);

        if (this.data.companyId) {
            this.loading.set(true);
            this.companyService.getCompany(this.data.companyId).subscribe( {
                next: data => {
                    this.company.data = data;
                    this.companyForm.patchValue(this.company.formData());
                },
                error: error => {this.messageService.errorMessage(error.message);}
            }).add(() => this.loading.set(false));
        }
    }

    onSubmit(): void {
        if (!this.companyForm.valid) {
            return;
        }

        const configJson = this.companyForm.controls['cashProviderConfigJson']?.value;
        if (configJson?.trim()) {
            try {
                JSON.parse(configJson);
            } catch {
                this.messageService.errorMessage('Неверный JSON в настройках кассового провайдера');
                return;
            }
        }

        this.loading.set(true);
        const request = this.company.data.id
            ? this.companyService.updateCompany(this.company.dataForUpdate(this.companyForm.value as ICompanyUpdate))
            :  this.companyService.createCompany(this.company.dataForCreate(this.companyForm.value as ICompanyCreate));

        request.subscribe({
            next: result => {
                this.messageService.successMessage('Группа успешно сохранена');
                this.dialogRef.close(result);},
            error: error => {this.messageService.errorMessage(error.message);}
        }).add(() => this.loading.set(false));
    }

    deleteCompany(id: number): void {
        if(!this.data) {return;}

        this.messageService.confirmMessage({
            title: 'Удаление компании',
            text: 'Вы действительно хотите удалить компанию?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.companyService.deleteCompanies([id]);
            }),
        ).subscribe({
            next: () => {
                this.messageService.successMessage('Компания успешно удалена');
                this.dialogRef.close();
            },
            error: () => this.messageService.errorMessage('При удалении компании возникла ошибка')
        });
    }

    private filter(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.timezones.filter(option => option.toLowerCase().includes(filterValue));
    }

}
